import { Box, Slider, Typography, useTheme } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';

import {
  BUILDER_CHROME_PRIMARY,
  BUILDER_CHROME_PRIMARY_RAIL,
  BUILDER_CHROME_PRIMARY_RING,
  LOGO_SIZE_DEFAULT,
  LOGO_SIZE_MAX,
  LOGO_SIZE_MIN,
  LOGO_SIZE_STEP,
} from '../../../../constants/templates';
import { useTemplateOverrideMode } from '../../../../hooks/useTemplateOverrideMode';
import { IBaseUploadControlProps } from '../../../../types/templates';
import { clampLogoSizePercent } from '../../../../utils/templates';
import { ResetButton } from '../../_controls_';

interface IProps extends IBaseUploadControlProps {
  globalValue: number;
  overrideValue?: number;
  defaultValue?: number;
  onChange: (percent: number) => void;
  onReset: () => void;
  onClearOverride?: () => void;
  onPreview?: (percent: number | null) => void;
  label?: string;
}

export const LogoSizeControl: React.FC<IProps> = ({
  mode,
  globalValue,
  overrideValue,
  defaultValue = LOGO_SIZE_DEFAULT,
  onChange,
  onReset,
  onClearOverride,
  onPreview,
  label = 'Logo size',
  dataAction,
}) => {
  const theme = useTheme();

  const { isPageOverrideMode, isOverridden, shouldClearOverride } = useTemplateOverrideMode({
    mode,
    overrideValue,
    onClearOverride,
  });

  const [draft, setDraft] = useState<string | null>(null);
  const [dragValue, setDragValue] = useState<number | null>(null);

  const committedValue = useMemo(() => {
    if (isOverridden && overrideValue !== undefined) {
      return overrideValue;
    }

    // Drafts persisted before logo sizing existed rehydrate without this field, so fall back to
    // the default rather than rendering "undefined" and dropping the slider out of controlled mode.
    return typeof globalValue === 'number' ? globalValue : defaultValue;
  }, [isOverridden, overrideValue, globalValue, defaultValue]);

  const displayValue = useMemo(() => (dragValue !== null ? dragValue : committedValue), [dragValue, committedValue]);

  const fieldValue = useMemo(() => (draft !== null ? draft : String(displayValue)), [draft, displayValue]);

  const showReset = useMemo(() => {
    if (isPageOverrideMode) {
      return isOverridden;
    }

    return committedValue !== defaultValue;
  }, [isPageOverrideMode, isOverridden, committedValue, defaultValue]);

  const readSliderValue = useCallback(
    (value: number | number[]) => clampLogoSizePercent(Array.isArray(value) ? value[0] : value),
    [],
  );

  const handleSliderDrag = useCallback(
    (_e: Event, value: number | number[]) => {
      const next = readSliderValue(value);
      setDraft(null);
      setDragValue(next);
      onPreview?.(next);
    },
    [readSliderValue, onPreview],
  );

  const handleSliderChange = useCallback(
    (_e: Event | React.SyntheticEvent, value: number | number[]) => {
      setDraft(null);
      setDragValue(null);
      onPreview?.(null);
      onChange(readSliderValue(value));
    },
    [onChange, onPreview, readSliderValue],
  );

  /*
   * Parse a typed percent, or null when the text isn't a usable number.
   *
   * Reads the string as a whole rather than stripping non-digits: stripping turns "120.9"
   * into "1209" (which then clamps to the 200% maximum) and "-50" into "50". A decimal is
   * rounded to the nearest whole percent; anything else - letters, "1e3", empty - is rejected.
   */
  const parsePercent = useCallback((raw: string): number | null => {
    const trimmed = raw.trim();
    if (!/^\d+(\.\d+)?$/.test(trimmed)) {
      return null;
    }

    return clampLogoSizePercent(Math.round(Number(trimmed)));
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      setDraft(next);

      // Clear the preview when the text isn't usable, otherwise the logo stays stuck at the last
      // parseable value while the field is empty or mid-edit.
      onPreview?.(parsePercent(next));
    },
    [onPreview, parsePercent],
  );

  const handleInputFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  }, []);

  const commitDraft = useCallback(() => {
    if (draft === null) {
      return;
    }

    const parsed = parsePercent(draft);
    setDraft(null);
    onPreview?.(null);

    if (parsed === null) {
      return;
    }

    onChange(parsed);
  }, [draft, onChange, onPreview, parsePercent]);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') {
        return;
      }

      commitDraft();
      (e.target as HTMLInputElement).blur();
    },
    [commitDraft],
  );

  const handleReset = useCallback(() => {
    setDraft(null);
    setDragValue(null);
    onPreview?.(null);

    if (shouldClearOverride) {
      onClearOverride!();
      return;
    }

    onReset();
  }, [shouldClearOverride, onClearOverride, onReset, onPreview]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="body2" fontWeight={theme.typography.fontWeightMedium} sx={{ lineHeight: 1.57, py: 1.5 }}>
          {label}
        </Typography>
        <Box sx={{ visibility: showReset ? 'visible' : 'hidden' }} aria-hidden={!showReset}>
          <ResetButton
            onClick={handleReset}
            dataAction={dataAction ? `${dataAction}_reset` : undefined}
            sx={{
              minWidth: 'auto',
              whiteSpace: 'nowrap',
              textTransform: 'none',
            }}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
        <Slider
          value={displayValue}
          min={LOGO_SIZE_MIN}
          max={LOGO_SIZE_MAX}
          step={LOGO_SIZE_STEP}
          onChange={handleSliderDrag}
          onChangeCommitted={handleSliderChange}
          aria-label="Logo size"
          data-action={dataAction ? `${dataAction}_slider` : undefined}
          sx={{
            flex: 1,
            color: BUILDER_CHROME_PRIMARY,
            height: 2,
            '& .MuiSlider-rail': { backgroundColor: BUILDER_CHROME_PRIMARY_RAIL, opacity: 1 },
            '& .MuiSlider-track': { border: 'none', backgroundColor: BUILDER_CHROME_PRIMARY },
            '& .MuiSlider-thumb': {
              width: 12,
              height: 12,
              backgroundColor: BUILDER_CHROME_PRIMARY,
              '&:hover, &.Mui-focusVisible': { boxShadow: `0 0 0 8px ${BUILDER_CHROME_PRIMARY_RING}` },
              '&.Mui-active': { boxShadow: `0 0 0 12px ${BUILDER_CHROME_PRIMARY_RING}` },
            },
          }}
        />
        <Box
          sx={{
            width: 64,
            height: 32,
            flexShrink: 0,
            boxSizing: 'border-box',
            borderRadius: 1,
            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1px',
          }}
        >
          <Box
            component="input"
            value={fieldValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={commitDraft}
            onKeyDown={handleInputKeyDown}
            inputMode="numeric"
            aria-label="Logo size %"
            data-action={dataAction ? `${dataAction}_input` : undefined}
            sx={{
              width: 30,
              border: 'none',
              outline: 'none',
              backgroundColor: 'transparent',
              fontSize: 14,
              textAlign: 'right',
              fontVariantNumeric: 'tabular-nums',
              color: 'text.primary',
            }}
          />
          <Box component="span" sx={{ fontSize: 14, color: 'text.secondary' }}>
            %
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
